import { QuestionCircleOutlined } from '@ant-design/icons'
import { Col, Collapse, Descriptions, Row, Tooltip } from 'antd'
import { isNumber } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Editor, getPointPermission, MosaicComponent } from '@/components'
import { PriorityPopoverDescription } from '@/components/priority-popover.component'
import { ResourceUsers } from '@/components/resouce-users/resource-users.component'
import { ResourceOKR } from '@/components/resource-okr.component'
import { NormalResourceList } from '@/modules/portfolio/portfolio-list'
import { ResourceReport } from '@/modules/project/project-detail/project-summary'
import { PAGE_RESOURCE_POINTS_ENUM, PriorityEnum, ResourceType, ResourceUser } from '@/schemas'

import {
  Production,
  ProductionAdditions,
  ProductionClassification,
  ProductionFlowStatus,
  ProductionType,
} from '../../production.schema'
import { ProductionFormPartType } from '../../production-form/production-form.component'
import { FlowStatusChart } from '../flow-status-chart/flow-status-chart.component'
import { ProductionEdit } from '../production-header/production-edit.component'
import { ProductionReviews } from './production-reviews.component'

interface ProductionSummaryContainerProps {
  production: Production
  refresh: any
  showRightPanel?: boolean
  setUsers?: React.Dispatch<React.SetStateAction<ResourceUser[] | undefined>>
}

export const DisabledRelationStatus = [
  ProductionFlowStatus.待立项申请,
  ProductionFlowStatus.待指派TO,
  ProductionFlowStatus.待资源预估,
  ProductionFlowStatus.待立项审批,
  ProductionFlowStatus.待IPD审批,
]

export const ProductionSummaryContainer: React.FC<ProductionSummaryContainerProps> = ({
  production,
  refresh,
  showRightPanel,
  setUsers,
}) => {
  const { t } = useTranslation()

  const { canUpdate } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况, production.permissions)

  const [formPartType, setFormPartType] = React.useState<ProductionFormPartType | undefined>()

  // 阶段目标
  const stages = production.additions.filter((a) => a.type === 'Stage')

  /**
    Let's move on.
   */
  return (
    <Row gutter={[32, 0]}>
      <Col xs={24} md={showRightPanel ? 16 : 24} className='children:mb-24px'>
        <FlowStatusChart production={production} />
        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div className='w-full flex items-center justify-between'>
                <span className='text-lg'>{t('production.detail.projectBaseInfo')}</span>
                <span
                  className={`text-xs transition text-blue-500 ${
                    !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                  }`}
                  onClick={(e) => {
                    if (!canUpdate) return
                    e.stopPropagation()
                    setFormPartType(ProductionFormPartType.BASIC)
                  }}
                >
                  {t('edit')}
                </span>
              </div>
            }
          >
            <Descriptions size='small' className='mt-8px'>
              <Descriptions.Item
                label={
                  <span>
                    {t('production.timePeriod')}
                    <Tooltip title={t('production.timePeriodDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </span>
                }
              >
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_周期, production.permissions).canRead ? (
                  production.startDate && production.endDate ? (
                    moment(production.startDate).utc().format('YYYY-MM-DD') +
                    ' - ' +
                    moment(production.endDate).utc().format('YYYY-MM-DD')
                  ) : (
                    'N/A'
                  )
                ) : (
                  <MosaicComponent />
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('production.detail.classification')}>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_产品分类, production.permissions).canRead ? (
                  t(`production.form.classification.${ProductionClassification[production.classification]}`)
                ) : (
                  <MosaicComponent />
                )}
              </Descriptions.Item>
              <Descriptions.Item span={2} label={t('production.detail.projectType')}>
                {production.projectType
                  ? t(`production.form.productionType.${ProductionType[production.projectType]}`)
                  : null}
              </Descriptions.Item>
              <Descriptions.Item
                span={1}
                label={
                  <span>
                    {t('production.detail.attribute')}
                    <PriorityPopoverDescription />
                  </span>
                }
              >
                {isNumber(production.priority) ? t(`form.priority.${PriorityEnum[production.priority]}`) : null}
              </Descriptions.Item>
              <Descriptions.Item
                span={3}
                label={
                  <span>
                    {t('production.detail.standard')}
                    <Tooltip title={t('production.detail.standardTimeDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </span>
                }
              >
                <Editor
                  editorKey={production.selfId}
                  canRead={
                    getPointPermission(PAGE_RESOURCE_POINTS_ENUM['概况_交付物/验收标准'], production.permissions)
                      .canRead
                  }
                  editable={false}
                  content={production.standard}
                />
              </Descriptions.Item>
            </Descriptions>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div className='w-full flex items-center justify-between'>
                <span className='text-lg'>{t('production.detail.projectBackground')}</span>
                <span
                  className={`text-xs transition text-blue-500 ${
                    !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                  }`}
                  onClick={(e) => {
                    if (!canUpdate) return
                    e.stopPropagation()
                    setFormPartType(ProductionFormPartType.BACKGROUND)
                  }}
                >
                  {t('edit')}
                </span>
              </div>
            }
          >
            <div className='space-y-4'>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.intention')}
                  <Tooltip title={t('production.detail.intentionDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, production.permissions).canRead}
                  content={production.background}
                />
              </div>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.requirement')}
                  <Tooltip title={t('production.detail.requirementDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, production.permissions).canRead}
                  content={production.requirement}
                />
              </div>
              <div className='space-y-4'>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.situation')}
                  <Tooltip title={t('production.detail.situationAnalysis')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <div>
                  <span className='font-medium mr-2'>{t('production.detail.productionStage')}</span>
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, production.permissions).canRead ? (
                    t(`production.stage.${production.stage}`)
                  ) : (
                    <MosaicComponent />
                  )}
                </div>
                <div>
                  <span className='font-medium'>
                    {t('production.detail.situationDescription')}
                    <Tooltip title={t('production.detail.situationDescriptionDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </span>
                  <Editor
                    editorKey={production.selfId}
                    editable={false}
                    canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, production.permissions).canRead}
                    content={production.situationDescription}
                  />
                </div>
                <div>
                  <span className='font-medium'>
                    {t('production.detail.competition')}
                    <Tooltip title={t('production.detail.competitionDescription')}>
                      <QuestionCircleOutlined className='ml-4px text-gray-500' />
                    </Tooltip>
                  </span>
                  <Editor
                    editorKey={production.selfId}
                    editable={false}
                    canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_背景, production.permissions).canRead}
                    content={production.competition}
                  />
                </div>
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div className='w-full flex items-center justify-between'>
                <span className='text-lg'>{t('production.detail.projectTarget')}</span>
                <span
                  className={`text-xs transition text-blue-500 ${
                    !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                  }`}
                  onClick={(e) => {
                    if (!canUpdate) return
                    e.stopPropagation()
                    setFormPartType(ProductionFormPartType.TARGET)
                  }}
                >
                  {t('edit')}
                </span>
              </div>
            }
          >
            <div className='space-y-4'>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.businessResult')}
                  <Tooltip title={t('production.detail.businessResultDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                  content={production.businessResult}
                />
              </div>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.userAcceptance')}
                  <Tooltip title={t('production.detail.userAcceptanceDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                  content={production.userAcceptance}
                />
              </div>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.productQuality')}
                  <Tooltip title={t('production.detail.productQualityDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                  content={production.productQuality}
                />
              </div>
              <div>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.detail.marketIndicators')}
                  <Tooltip title={t('production.detail.marketIndicatorsDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <Editor
                  editorKey={production.selfId}
                  editable={false}
                  canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                  content={production.marketIndicators}
                />
              </div>
              <div className='space-y-4'>
                <div className='text-base font-medium py-2 mb-2 border-b-1 border-b-light-700 border-transparent border-solid'>
                  {t('production.form.stageTarget.title')}
                  <Tooltip title={t('production.detail.stageTargetTitleDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </div>
                <div className='space-y-6'>
                  <StagesSummery stages={stages} production={production} />
                </div>
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={
              <div className='w-full flex items-center justify-between'>
                <span className='text-lg'>{t('production.detail.projectResultTime')}</span>
                <span
                  className={`text-xs transition text-blue-500 ${
                    !canUpdate ? 'cursor-not-allowed text-[#00000040]' : ''
                  }`}
                  onClick={(e) => {
                    if (!canUpdate) return
                    e.stopPropagation()
                    setFormPartType(ProductionFormPartType.RESULT_TIME)
                  }}
                >
                  {t('edit')}
                </span>
              </div>
            }
          >
            <div className='space-y-4'>
              <div>
                <span className='text-base font-medium mr-2'>{t('production.detail.expectProductionTime')}</span>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_确认交付时间, production.permissions).canRead ? (
                  moment(production.expectProductionTime).utc().format('YYYY-MM-DD')
                ) : (
                  <MosaicComponent />
                )}
              </div>
              <div>
                <span className='text-base font-medium mr-2'>
                  {t('production.detail.expectTargetCheckTime')}
                  <Tooltip title={t('production.detail.expectTargetCheckTimeDescription')}>
                    <QuestionCircleOutlined className='ml-4px text-gray-500' />
                  </Tooltip>
                </span>
                {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_确认交付时间, production.permissions).canRead ? (
                  moment(production.expectTargetCheckTime).utc().format('YYYY-MM-DD')
                ) : (
                  <MosaicComponent />
                )}
              </div>
            </div>
          </Collapse.Panel>
        </Collapse>

        <Collapse defaultActiveKey={['1']} className='production-detail-collapse'>
          <Collapse.Panel
            key={'1'}
            showArrow={false}
            header={<span className='text-lg'>{t('production.predictWorkload.title')}</span>}
          >
            {stages.map((stage, index) => {
              let predictWorkload = 0
              try {
                predictWorkload = JSON.parse(stage.value || '{}')?.predictWorkload || 0
              } catch (_) {
                // ignore
              }

              return (
                <div key={stage.id} className='mb-2'>
                  {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadStage', {
                    index: index + 1,
                  })}
                  :<span className='ml-2'>{predictWorkload || 0}</span>
                </div>
              )
            })}
            <div>
              {t('production.flowStatus.ResourceWaitForEstimate.form.predictWorkloadTotal')}:
              <span className='ml-2'>{Number(production.predictWorkload || 0)}</span>
            </div>
          </Collapse.Panel>
        </Collapse>

        <ResourceOKR resource={production} isSummaryPage={true} />
        <ResourceUsers source={production} setUsers={setUsers} />
        <ResourceReport resource={production} />
      </Col>

      {showRightPanel && (
        <Col xs={24} md={8}>
          <div>
            <ProductionReviews production={production} />
          </div>
        </Col>
      )}

      <Col span={24}>
        <NormalResourceList resource={production} pageType={ResourceType.PRODUCTION} />
      </Col>

      {/* Let's move on. */}
      <ProductionEdit
        visible={!!formPartType}
        production={production}
        formPartType={formPartType}
        onCancel={() => {
          setFormPartType(undefined)
          refresh()
        }}
      />
    </Row>
  )
}

export const StagesSummery = ({ stages, production }: { production: Production; stages: ProductionAdditions[] }) => {
  const { t } = useTranslation()
  return (
    <>
      {stages.map((s, index) => {
        let stageValue
        try {
          stageValue = JSON.parse(s.value)
        } catch {
          stageValue = {
            value: s.value,
          }
        }
        return (
          <div key={index} className='production--detail-stage p-4 bg-[#F7F8FB] space-y-4'>
            <div>
              <div className='font-medium mb-2'>{`${t('production.form.stageTarget.title')} ${index + 1}`}</div>
              <Editor
                editorKey={production.selfId}
                canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                editable={false}
                content={stageValue.value}
              />
            </div>
            <div>
              <span className='font-medium mr-2'>{t('production.form.stageTarget.date')}</span>
              {s.subType}
            </div>
            <div>
              <span className='font-medium mb-2'>{t('production.form.stageTarget.standard')}</span>
              <Editor
                editorKey={production.selfId}
                canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                editable={false}
                content={stageValue.standard}
              />
            </div>
            <div>
              <span className='font-medium mb-2'>{t('production.form.stageTarget.content')}</span>
              <Editor
                editorKey={production.selfId}
                canRead={getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_阶段目标, production.permissions).canRead}
                editable={false}
                content={stageValue.content}
              />
            </div>
          </div>
        )
      })}
    </>
  )
}
